import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { cloneDeep } from 'lodash-es';
import { ModalBaseDirective } from '$core/base/_modal.component';
import { focusFirstInvalidField } from '$core/helpers/focus-first-invaild-field';
import { handleFormHttpError } from '$core/helpers/server-errors-form-handler';
import { trackByFn } from '$core/helpers/track-by-fn';
import { triggerFormValidation } from '$core/helpers/trigger-form-validation';
import { OldAddress } from '$core/models/old-address';
import { UserTypes } from '$core/models/user';
import { AddressesService } from '$core/services/addresses.service';
import { SessionService } from '$core/services/session.service';
import { SwalService } from '$core/shell/swal/swal.service';
import { FioValidators } from '$shared/validators/fio.validators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-manage-address',
  templateUrl: './manage-address.component.html',
  styleUrls: ['./manage-address.component.scss'],
})
export class ManageAddressComponent
  extends ModalBaseDirective
  implements OnInit, AfterViewInit
{
  public readonly trackByFn = trackByFn;
  public item: OldAddress = new OldAddress();
  public isLoading: boolean;
  public defaultChanged: boolean;
  public types: string[] = [];
  public form: FormGroup;
  public error: HttpErrorResponse;

  @ViewChild('formEl', { static: false })
  private formEl: ElementRef<HTMLFormElement>;

  constructor(
    protected modalRef: BsModalRef,
    protected modalService: BsModalService,
    protected translateService: TranslateService,
    protected swalService: SwalService,
    protected addressService: AddressesService,
    private fb: FormBuilder,
    private sessionService: SessionService,
    private destroyRef: DestroyRef
  ) {
    super(modalRef, modalService, translateService);
  }

  public get isLegal(): boolean {
    return this.sessionService.session
      ? this.sessionService.session.type === UserTypes.Legal
      : false;
  }

  public ngOnInit(): void {
    this.initForms();
    this.setSessionSubscriber();
    this.addressSubscriber();
  }

  public addressSubscriber(): void {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.error = undefined;
      });
  }

  public ngAfterViewInit(): void {
    this.addressTypeSubscriber();
  }

  public addressTypeSubscriber(): void {
    this.form
      .get('address_type')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (value: 'address' | 'on_demand' | 'subscriber_box') => {
          this.setAddressTypeValidation(value, true);
        },
      });
  }

  public setAddressTypeValidation(
    value: 'address' | 'on_demand' | 'subscriber_box',
    clearAddress = false
  ): void {
    if (clearAddress) {
      this.form.get('contact_data').reset();
    }
    this.form.get('cell_number').clearValidators();
    this.form.get('building').clearValidators();

    switch (value) {
      case 'address':
        this.form.get('building').setValidators([Validators.required]);
        break;
      case 'subscriber_box':
        this.form
          .get('cell_number')
          .setValidators([
            Validators.required,
            Validators.maxLength(6),
            Validators.min(1),
            Validators.pattern('^[0-9]*$'),
          ]);
        break;
    }
  }

  public async sendForm(): Promise<void> {
    this.form['submitted'] = true;
    this.error = undefined;
    triggerFormValidation(this.form);

    if (this.form.invalid) {
      focusFirstInvalidField(this.formEl);
      return;
    }

    const body: OldAddress = this.createBody();

    if (!this.isLegal) {
      delete body.name;
    } else {
      delete body.first_name;
      delete body.last_name;
      delete body.second_name;
    }

    switch (body.address_type) {
      case 'address':
        delete body.cell_number;
        break;
      case 'on_demand':
        delete body.building;
        delete body.housing;
        delete body.apartment;
        delete body.cell_number;
        break;
      case 'subscriber_box':
        delete body.building;
        delete body.housing;
        delete body.apartment;
        body.cell_number = body.cell_number.toString();
        break;
    }

    this.isLoading = true;

    const { swal } = this.swalService;

    try {
      let add: OldAddress;

      if (this.item.id) {
        body.id = this.item.id;
        add = await this.addressService
          .updateItem<OldAddress>(body)
          .toPromise();
        this.defaultChanged = add.is_default;
      } else {
        add = await this.addressService
          .createItem<OldAddress>(body)
          .toPromise();
        this.defaultChanged = this.item.is_default !== add.is_default;
      }

      this.item = add;
      swal.icon = 'success';
      swal.title = body.id
        ? this.translate('ADDRESS_UPDATE_SUCCESS')
        : this.translate('ADDRESS_CREATE_SUCCESS');
      swal.text = '';
      swal.fire();
      this.hide('saved');
    } catch (error) {
      const res: HttpErrorResponse = (this.error = error);
      if (res.status === 422) {
        const errors = this.mapApiErrorOnForm(res);

        handleFormHttpError(errors, this.form);
        focusFirstInvalidField(this.formEl);
      } else {
        swal.icon = 'error';
        swal.title = this.translate('AN_ERROR_HAS_OCCURRED');
        swal.text = res.statusText;
        swal.fire();
      }
    } finally {
      this.isLoading = false;
    }
  }

  private setSessionSubscriber(): void {
    this.sessionService.session$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        if (res && !this.form.get('name').value && res.name) {
          this.form.get('name').setValue(res.name);
        }
      });
  }

  private initForms(): void {
    this.form = this.fb.group({
      type: [this.item.type || '', Validators.required],
      name: [this.item.name || '', this.isLegal ? Validators.required : null],
      last_name: [this.item.last_name || ''],
      first_name: [this.item.first_name || ''],
      second_name: [this.item.second_name || ''],
      address_type: [this.item.address_type || 'address', Validators.required],
      cell_number: [this.item.address ? +this.item.cell_number : null],
      address: [this.item.address, Validators.required],
      building: [this.item.house, Validators.required],
      housing: [this.item.building],
      apartment: [this.item.flat],
      postcode_id: [this.item.postcode_id],

      comment: [this.item.comment],
      is_default: [this.item.is_default],
    });

    if (!this.isLegal) {
      this.addValidatorsForIndividual();
    }

    this.setAddressTypeValidation(this.item.address_type || 'address');
  }

  private mapApiErrorOnForm(res: HttpErrorResponse): HttpErrorResponse {
    const { house, building, flat, ...restErrors } = res.error.errors;
    const errors = {
      ...restErrors,
      building: house,
      housing: building,
      apartment: flat,
    };
    const resClone = cloneDeep(res);

    resClone.error.errors = errors;

    return resClone;
  }

  private createBody(): OldAddress {
    const body = this.form.value;
    const { controls } = this.form;

    body.house = controls.building.value;
    body.building = controls.housing.value;
    body.flat = controls.apartment.value;

    return body;
  }

  private addValidatorsForIndividual(): void {
    this.form.get('first_name').setValidators([FioValidators.firstName()]);
    this.form.get('last_name').setValidators([FioValidators.lastName()]);
    this.form.get('second_name').setValidators([FioValidators.secondName()]);
    this.form.setValidators([FioValidators.fullName()]);
  }
}
